import { TableV2 } from '@anytag/shared-atoms';
import { useTableMaxHeight } from '@anytag/shared-hooks';
import { CampaignPostDetailsModal } from '@anytag/shared-molecules';
import { useEngagementPermissions, useUserRoles } from '@anytag/auth';
import {
  CampaignType,
  EngagementPost,
  EngagementDetail,
  namedOperations,
  EngagementCampaignPostStatus
} from '@anytag/graphql';
import { ActionBar } from './ActionBar';
import { FooterNode } from './FooterNode';
import { useRejectEngagementPost, useEngagementPostsListTable, useUpdateEngagementPostsStatus } from './hooks';

export interface EngagementPostsListProps {
  loading?: boolean;
  engagement: EngagementDetail;
  posts: readonly EngagementPost[];
}

export const EngagementPostsList = ({ posts, loading, engagement }: EngagementPostsListProps) => {
  const { isTtcmCampaign } = engagement;
  const { offsetRef, defaultTableMaxHeight } = useTableMaxHeight();
  const { hideApproveRejectInappropriatePostBtn } = useEngagementPermissions();
  const { isAdvertiser, isAdminStaffAgencyTalentAgencyPartner } = useUserRoles();
  const { columns, postsActions } = useEngagementPostsListTable({ posts, engagement });
  const { handleRejectPost, RejectEngagementPostModal } = useRejectEngagementPost({
    refetchQueries: [namedOperations.Query.AllEngagementPosts, namedOperations.Query.EngagementPostDetail],
    isTtcmCampaign
  });
  const { handleSelectStatus, UpdateEngagementPostStatusModal } = useUpdateEngagementPostsStatus({
    refetchQueries: [namedOperations.Query.AllEngagementPosts, namedOperations.Query.EngagementPostDetail]
  });

  return (
    <>
      <div css={{ position: 'relative' }}>
        <TableV2
          ref={offsetRef}
          loading={loading}
          maxHeight={defaultTableMaxHeight}
          columns={columns}
          data={posts}
          rowHeight="61px"
          noTopBorderRadius
          stickyHeader
          hasBorder
        />

        <ActionBar {...postsActions} isTtcmCampaign={isTtcmCampaign} />
      </div>

      <CampaignPostDetailsModal
        posts={posts.map<EngagementPost & { campaignType: CampaignType.ENGAGEMENT }>((post) => ({
          ...post,
          campaignType: CampaignType.ENGAGEMENT
        }))}
        engPostProps={{
          footer: ({ tab, postDetails, selectedPost }) => {
            if (!postDetails || tab !== 'Details') return null;

            const isReviewing = postDetails.status === EngagementCampaignPostStatus.REVIEWING;
            const hasReportInappropriate =
              (isAdminStaffAgencyTalentAgencyPartner || isAdvertiser) &&
              postDetails.status &&
              [EngagementCampaignPostStatus.EFFECTIVE, EngagementCampaignPostStatus.WARNING_SOLVED].includes(
                postDetails.status
              ) &&
              !hideApproveRejectInappropriatePostBtn;

            return selectedPost && !hideApproveRejectInappropriatePostBtn && (isReviewing || hasReportInappropriate) ? (
              <FooterNode
                isReviewing={isReviewing}
                postDetails={postDetails}
                selectedPost={selectedPost}
                handleRejectPost={handleRejectPost}
                handleSelectStatus={handleSelectStatus}
                hasReportInappropriate={hasReportInappropriate}
              />
            ) : null;
          }
        }}
      />

      {/* Reset selected items on success to clear unchangable posts were included */}
      <RejectEngagementPostModal onSuccess={() => postsActions.setSelectedItemIds([])} />
      <UpdateEngagementPostStatusModal onSuccess={() => postsActions.setSelectedItemIds([])} />
    </>
  );
};
